// The draft the way the sheet reads: start times by fields in the division
// colors of the sheet's key, with the checks and the model's warnings beside
// it. A click on a game fixes its division or start in the draft

import { useEffect, useMemo, useRef, useState } from 'react';
import moment from 'moment';
import ScheduleImportFix from './ScheduleImportFix';
import { useModal } from './ModalProvider';
import { time24To12H } from '../utils/misc';
import { GameTypes } from '../utils/enums';
import './ScheduleImportPreview.scss';

const markLabels = { added: 'new', changed: 'changed' };

function PreviewGame({
  index,
  game,
  original,
  fieldName,
  color,
  divisions,
  issues,
  isFixed,
  mark,
  highlighted,
  onFix,
}) {
  const { openModal } = useModal();
  const ref = useRef(null);

  const severity = issues.some((i) => i.severity === 'error')
    ? 'error'
    : issues.length > 0
      ? 'warning'
      : '';
  const sides = `${game.side1 || '?'} – ${game.side2 || '?'}`;
  const tags = [markLabels[mark], isFixed && 'fixed'].filter(Boolean);

  const open = () =>
    openModal({
      header: `Fix game: ${time24To12H(original.start)}, ${fieldName}`,
      body: (
        <ScheduleImportFix
          game={game}
          original={original}
          divisions={divisions}
          issues={issues}
          onSave={(fix) => onFix(index, fix)}
        />
      ),
      ancorRef: ref,
    });

  const title = [
    game.division,
    game.type === GameTypes.PLAYOFF ? 'Playoff' : 'Pool game',
    `${sides}: the sheet's sides, not saved`,
    ...issues.map((i) => i.message),
  ].join('\n');

  return (
    <div
      ref={ref}
      className={
        'game' +
        (severity ? ` ${severity}` : '') +
        (isFixed ? ' fixed' : '') +
        (highlighted ? ' highlighted' : '')
      }
      style={color && { borderLeftColor: color, backgroundColor: `${color}1A` }}
      data-index={index}
      title={title}
      role='button'
      tabIndex={0}
      onClick={open}
      onKeyDown={(e) => {
        if (e.key !== 'Enter' && e.key !== ' ') return;
        e.preventDefault();
        open();
      }}
    >
      <p className='division'>
        {severity && <span className='fa-flag' />}
        {game.division}
      </p>
      <p className='sides'>
        <span className='unsaved'>{sides}</span>
        {tags.map((tag) => (
          <span key={tag} className={`tag ${tag}`}>
            {tag}
          </span>
        ))}
      </p>
    </div>
  );
}

export default function ScheduleImportPreview({
  draft,
  original,
  checks,
  fixes,
  marks,
  onFix,
  isChecking,
  checkError,
}) {
  const [highlighted, setHighlighted] = useState(null);
  const gridRef = useRef(null);

  const { event, fields, divisions, games, total } = draft;
  const divisionNames = useMemo(
    () => divisions.map((d) => d.name),
    [divisions],
  );
  const colors = useMemo(
    () => new Map(divisions.map((d) => [d.name, d.color])),
    [divisions],
  );

  // A game the model put on a field the sheet lacks still needs a column
  const columns = useMemo(() => {
    const known = new Set(fields.map((f) => f.key));
    const strays = [
      ...new Set(games.map((g) => g.field).filter((f) => !known.has(f))),
    ];
    return [...fields, ...strays.map((key) => ({ key, name: key }))];
  }, [fields, games]);

  const rows = useMemo(() => {
    const starts = [
      ...new Set([
        ...draft.slots.map((s) => s.start),
        ...games.map((g) => g.start),
      ]),
    ].sort();
    return starts.map((start) => ({
      start,
      games: games
        .map((game, index) => ({ game, index }))
        .filter(({ game }) => game.start === start),
    }));
  }, [draft.slots, games]);

  // Warnings name the games as the model read them, before any fix
  const warnings = useMemo(() => {
    const at = new Map();
    original.games.forEach((g, i) =>
      at.set(g.key, [...(at.get(g.key) ?? []), i]),
    );
    return original.warnings.map((w) => ({ ...w, games: at.get(w.key) ?? [] }));
  }, [original]);

  const issuesByGame = useMemo(() => {
    const map = new Map();
    const add = (i, issue) => map.set(i, [...(map.get(i) ?? []), issue]);
    for (const w of warnings)
      for (const i of w.games)
        add(i, { severity: 'warning', message: w.message });
    for (const c of checks)
      if (!c.passed)
        for (const i of c.games)
          add(i, { severity: c.severity, message: c.message });
    return map;
  }, [warnings, checks]);

  const failed = [
    ...checks.filter((c) => !c.passed && c.severity === 'error'),
    ...checks.filter((c) => !c.passed && c.severity !== 'error'),
  ];
  const passed = checks.filter((c) => c.passed);

  // An object, so that Show on the same game scrolls to it again
  useEffect(() => {
    if (!highlighted) return;
    gridRef.current
      ?.querySelector(`[data-index="${highlighted.index}"]`)
      ?.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
        inline: 'center',
      });
  }, [highlighted]);

  const showButton = (indices) =>
    indices.length > 0 && (
      <button
        type='button'
        className='small transparent'
        onClick={() => setHighlighted({ index: indices[0] })}
      >
        Show
      </button>
    );

  return (
    <section className='schedule-import-preview'>
      <div className='summary'>
        <p className='font-16 font-medium'>{event.name || 'Untitled event'}</p>
        <p>
          {[
            event.date && moment(event.date).format('ddd, MMM D, YYYY'),
            event.venue,
            `${games.length} game${games.length === 1 ? '' : 's'} on ` +
              `${fields.length} field${fields.length === 1 ? '' : 's'}`,
            total != null && `the sheet's total: ${total}`,
          ]
            .filter(Boolean)
            .join(' · ')}
        </p>
      </div>

      <div className='preview-body'>
        <div className='sheet'>
          <ul className='legend'>
            {divisions.map((d) => (
              <li key={d.name} style={d.color && { borderLeftColor: d.color }}>
                {d.name}
                {d.teams != null && (
                  <span className='gray-700'> · {d.teams} teams</span>
                )}
              </li>
            ))}
          </ul>
          <p className='note font-12'>
            The sides in gray are the sheet's labels and are not saved: every
            game is imported TBD against TBD. Click a game to fix its division
            or start.
          </p>
          <div className='grid-wrap' ref={gridRef}>
            <div className='grid' style={{ '--columns': columns.length }}>
              <div className='row head'>
                <div className='time' />
                {columns.map((field) => (
                  <p key={field.key} className='cell' title={field.name}>
                    {field.name}
                  </p>
                ))}
              </div>
              {rows.map(({ start, games: here }) => (
                <div key={start} className='row'>
                  <p className='time'>{time24To12H(start)}</p>
                  {columns.map((field) => {
                    const inCell = here.filter(
                      ({ game }) => game.field === field.key,
                    );
                    return (
                      <div
                        key={field.key}
                        className={'cell' + (inCell.length ? '' : ' empty')}
                      >
                        {inCell.map(({ game, index }) => (
                          <PreviewGame
                            key={index}
                            index={index}
                            game={game}
                            original={original.games[index]}
                            fieldName={field.name}
                            color={colors.get(game.division)}
                            divisions={divisionNames}
                            issues={issuesByGame.get(index) ?? []}
                            isFixed={!!fixes[index]}
                            mark={marks.get(index)}
                            highlighted={highlighted?.index === index}
                            onFix={onFix}
                          />
                        ))}
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className='issues'>
          <h6>
            Checks{isChecking && <span className='gray-600'> · checking…</span>}
          </h6>
          <ul className='checks'>
            {failed.map((c) => (
              <li key={c.code} className={`check ${c.severity}`}>
                <span className='fa-cross-circle' />
                <p>
                  {c.message}
                  {c.severity === 'warning' && (
                    <span className='gray-700'> (a warning)</span>
                  )}
                </p>
                {showButton(c.games)}
              </li>
            ))}
            {passed.length > 0 && (
              <li
                className='check passed'
                title={passed.map((c) => c.message).join('\n')}
              >
                <span className='fa-check-circle' />
                <p>
                  {failed.length > 0
                    ? `${passed.length} other checks passed`
                    : `All ${passed.length} checks passed`}
                </p>
              </li>
            )}
          </ul>
          {checkError && (
            <p className='check-error font-12'>Not re-checked: {checkError}</p>
          )}
          {warnings.length > 0 && (
            <>
              <h6>Unsure readings</h6>
              <ul className='warnings'>
                {warnings.map((w, i) => (
                  <li key={i} className='warning'>
                    <p>
                      {w.message}
                      {w.games.length === 0 && w.cells.length > 0 && (
                        <span className='gray-700'>
                          {' '}
                          ({w.cells.join(', ')})
                        </span>
                      )}
                    </p>
                    {showButton(w.games)}
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>
      </div>
    </section>
  );
}
